import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Modal, Form, Button } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import {
  BranchPatientsSelect,
  PatientEffectiveOrderSelect,
} from "../forms/AutoCompleteFormItem";
import { useSelector } from "react-redux";
import moment from "moment";

const TransferModal = ({
  visible,
  onHide,
  onTransfer,
  items,
  selectedRowKeys,
}) => {
  const [form] = Form.useForm();
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const intl = useIntl();
  let location = useLocation();
  const isAdmin = location.pathname.includes("admin");
  let { branchId } = useParams();
  const eventsToTransfer =
    items?.filter((i) => selectedRowKeys.includes(i.id)) || [];
  const startDate =
    eventsToTransfer.length > 0 &&
    Math.min(...eventsToTransfer.map((e) => new Date(e.date)));
  const endDate =
    eventsToTransfer.length > 0 &&
    Math.max(...eventsToTransfer.map((e) => new Date(e.date)));

  const activePatientFilter = {
    $and: [
      {
        $or: [{ status_id: 1 }],
      },
    ],
  };

  const onPatientChange = (patientId) => {
    setSelectedPatientId(patientId);
    form.setFieldsValue({ order_id: null });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onTransfer(values);
      form.resetFields();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const filterItems = (items) => {
    if (!items) return [];
    return items.filter((item) => {
      // Check effective_from is less than or equal to startDate
      const isEffectiveFromValid = moment(item.effective_from).isSameOrBefore(
        moment(startDate)
      );

      // Check effective_to is either null or greater than or equal to endDate
      const isEffectiveToValid =
        !item.effective_to || // null check
        moment(item.effective_to).isSameOrAfter(moment(endDate));

      // Filter out supporting community and day center orders
      const isValidOrderType =
        item.order_service_type.supporting_community !== true &&
        item.order_service_type.day_center !== true;

      return isEffectiveFromValid && isEffectiveToValid && isValidOrderType;
    });
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedPatientId(null);
    }
  }, [visible, form]);

  return (
    <Modal
      title={intl.formatMessage({ id: "app.scheduling.transfer.title" })}
      open={visible}
      onCancel={onHide}
      footer={[
        <Button key="cancel" onClick={onHide}>
          <FormattedMessage id="app.actions.cancel" />
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          <FormattedMessage id="app.actions.transfer_events" />
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <BranchPatientsSelect
          name="patient_id"
          category="tasks"
          getPatient={branchId !== null || isAdmin}
          extraParams={{
            scopePrefix,
            filter: activePatientFilter,
          }}
          required={true}
          onChange={onPatientChange}
          withId={true}
        />

        <PatientEffectiveOrderSelect
          name="order_id"
          category="scheduling"
          required={true}
          params={{
            patientId: selectedPatientId,
          }}
          extraParams={{
            scopePrefix,
          }}
          filterItems={filterItems}
          waitForFetch={!selectedPatientId}
          disabled={!selectedPatientId}
        />
      </Form>
    </Modal>
  );
};

export default TransferModal;
